import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { StammkundeService } from '../../../Services/stammkunde.service';
import {
  Auftrag,
  AuftragAssignment,
  AUFTRAG_STATUS_LABELS,
  AUFTRAG_STATUS_ORDER,
  AuftragStatus,
  EinsatzMaterial,
  MATERIAL_STATUS_LABELS,
  MyStammkundeMembership,
  PlannableUser,
  StammkundeWorker,
  TIME_ENTRY_STATUS_LABELS,
  ZeitEintrag
} from '../../../Models/stammkunde.model';
import { AuthService } from '../../../api-authorization/authorize.service';
import { AuftragFormComponent } from '../auftrag-form/auftrag-form.component';
import { EinsatzFormComponent } from '../einsatz-form/einsatz-form.component';
import { ZeitEintragFormComponent, ZeitPerson } from '../zeit-eintrag-form/zeit-eintrag-form.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';

interface DayGroup {
  day: Date;
  items: AuftragAssignment[];
}

interface TimeDayGroup {
  day: Date;
  items: ZeitEintrag[];
  hours: number;
}

/**
 * Detailseite eines Stammkunden-Auftrags: sichtbarer Status-Workflow (Stepper),
 * Auftragsdaten und die Einsätze nach Tagen gruppiert. Der Bauleiter plant hier
 * oder springt direkt ins Planungsboard.
 */
@Component({
  selector: 'app-auftrag-detail',
  templateUrl: './auftrag-detail.component.html',
  styleUrls: ['./auftrag-detail.component.css', '../stammkunden-theme.css']
})
export class AuftragDetailComponent implements OnInit {
  customerId!: number;
  auftragId!: number;
  auftrag: Auftrag | null = null;
  assignments: AuftragAssignment[] = [];
  users: PlannableUser[] = [];
  workers: StammkundeWorker[] = [];
  myMemberships: MyStammkundeMembership[] = [];
  loading = true;
  statusSaving = false;

  /** Einsätze nach Tagen gruppiert (memoized – wird bei jeder Datenänderung neu aufgebaut). */
  dayGroups: DayGroup[] = [];

  /** Material-Übersicht: Gesamtsummen (gleiches Material + Einheit zusammengezählt). */
  materialTotals: EinsatzMaterial[] = [];
  /** Material-Übersicht: nur Tage/Einsätze, die Material haben. */
  materialDayGroups: DayGroup[] = [];
  materialView: 'gesamt' | 'einsatz' = 'gesamt';

  /** Stunden-Erfassung (ohne Preise) – nach Tagen gruppiert. */
  timeEntries: ZeitEintrag[] = [];
  timeDayGroups: TimeDayGroup[] = [];
  totalHours = 0;

  readonly statusLabels = AUFTRAG_STATUS_LABELS;
  readonly statusSteps = AUFTRAG_STATUS_ORDER;
  readonly timeStatusLabels = TIME_ENTRY_STATUS_LABELS;
  readonly materialStatusLabels = MATERIAL_STATUS_LABELS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stammkundeService: StammkundeService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.customerId = Number(params.get('id'));
      this.auftragId = Number(params.get('auftragId'));
      this.load();
    });
  }

  /** Planen darf der Bauleiter DIESES Stammkunden oder Admin/SuperAdmin. */
  get canManage(): boolean {
    if (this.authService.isSuperAdmin() || this.authService.isAdmin()) return true;
    return this.myMemberships.some(m => m.customerId === this.customerId && m.role === 'Bauleiter');
  }

  load(): void {
    this.loading = true;
    forkJoin({
      auftrag: this.stammkundeService.getAuftrag(this.auftragId),
      assignments: this.stammkundeService.getAssignments(this.auftragId),
      members: this.stammkundeService.getMembers(this.customerId),
      workers: this.stammkundeService.getWorkers(this.customerId),
      permissions: this.authService.getMyPermissions()
    }).subscribe({
      next: result => {
        this.auftrag = result.auftrag;
        this.assignments = result.assignments ?? [];
        this.rebuildDayGroups();
        // Planbar ist nur das Team dieses Stammkunden (keine globale Benutzerliste).
        this.users = (result.members ?? []).map(m => ({ id: m.userId, userName: m.userName, email: m.email }));
        this.workers = result.workers ?? [];
        this.myMemberships = result.permissions?.stammkundenMemberships ?? [];
        this.loading = false;
        // Stunden separat laden: fehlt die Tabelle (Migration noch nicht eingespielt),
        // bleibt der Auftrag trotzdem bedienbar.
        this.loadTimeEntries();
      },
      error: () => {
        this.snackBar.open('Fehler beim Laden des Auftrags', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  // ── Status-Workflow ──

  statusIndex(status: AuftragStatus): number {
    return this.statusSteps.indexOf(status);
  }

  get currentStatusIndex(): number {
    return this.auftrag ? this.statusIndex(this.auftrag.status) : 0;
  }

  /** Klick auf einen Workflow-Schritt setzt den Status (nur Bauleiter/Admin). */
  setStatus(status: AuftragStatus): void {
    if (!this.canManage || !this.auftrag || this.auftrag.status === status || this.statusSaving) return;

    const payload = {
      customerId: this.auftrag.customerId,
      title: this.auftrag.title,
      description: this.auftrag.description,
      orderNumber: this.auftrag.orderNumber,
      receivedDate: this.auftrag.receivedDate,
      startDate: this.auftrag.startDate,
      dueDate: this.auftrag.dueDate,
      location: this.auftrag.location,
      primaryWarehouseId: this.auftrag.primaryWarehouseId,
      status
    };

    this.statusSaving = true;
    this.stammkundeService.updateAuftrag(this.auftrag.id, payload).subscribe({
      next: updated => {
        this.statusSaving = false;
        this.auftrag = updated;
        this.snackBar.open(`Status: ${this.statusLabels[status]}`, 'OK', { duration: 3000 });
      },
      error: err => {
        this.statusSaving = false;
        const message = err?.error?.message || 'Status konnte nicht gesetzt werden.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  // ── Einsätze (nach Tagen gruppiert) ──

  private rebuildDayGroups(): void {
    const groups = new Map<string, DayGroup>();
    const sorted = [...this.assignments].sort((a, b) => a.start.localeCompare(b.start));
    for (const e of sorted) {
      const d = new Date(e.start);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!groups.has(key)) {
        groups.set(key, { day: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [] });
      }
      groups.get(key)!.items.push(e);
    }
    this.dayGroups = Array.from(groups.values());

    // Material-Übersicht: Gesamtsummen (Name + Einheit als Schlüssel) ...
    const totals = new Map<string, EinsatzMaterial>();
    for (const e of this.assignments) {
      for (const m of e.materials ?? []) {
        const key = `${m.name.toLowerCase()}|${m.unit}`;
        const existing = totals.get(key);
        if (existing) {
          existing.quantity += m.quantity;
        } else {
          totals.set(key, { name: m.name, quantity: m.quantity, unit: m.unit });
        }
      }
    }
    this.materialTotals = Array.from(totals.values())
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));

    // ... und je Einsatz-Termin (nur Einsätze mit Material).
    this.materialDayGroups = this.dayGroups
      .map(g => ({ day: g.day, items: g.items.filter(e => (e.materials?.length ?? 0) > 0) }))
      .filter(g => g.items.length > 0);
  }

  // ── Stunden-Erfassung ──

  private rebuildTimeGroups(): void {
    const groups = new Map<string, TimeDayGroup>();
    const sorted = [...this.timeEntries]
      .sort((a, b) => a.workDate.localeCompare(b.workDate) || a.personName.localeCompare(b.personName, 'de'));
    let total = 0;
    for (const t of sorted) {
      total += t.hours;
      const d = new Date(t.workDate);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!groups.has(key)) {
        groups.set(key, { day: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [], hours: 0 });
      }
      const g = groups.get(key)!;
      g.items.push(t);
      g.hours += t.hours;
    }
    this.timeDayGroups = Array.from(groups.values());
    this.totalHours = total;
  }

  get hasTimeEntries(): boolean {
    return this.timeEntries.length > 0;
  }

  timeStatusLabel(status: string): string {
    return (this.timeStatusLabels as Record<string, string>)[status] ?? status;
  }

  /** Personenauswahl für die Stundenerfassung: Team (mit Login) + Arbeiter (ohne Login). */
  private buildPeople(): ZeitPerson[] {
    const people: ZeitPerson[] = [];
    for (const u of this.users) {
      people.push({ label: u.userName || u.email || u.id, personUserId: u.id });
    }
    for (const w of this.workers) {
      people.push({ label: w.name, workerId: w.id });
    }
    return people;
  }

  openZeitForm(entry?: ZeitEintrag): void {
    if (!this.canManage || !this.auftrag) return;
    const dialogRef = this.dialog.open(ZeitEintragFormComponent, {
      width: '520px',
      maxWidth: '96vw',
      data: {
        auftrag: this.auftrag,
        assignmentId: entry?.assignmentId ?? null,
        people: this.buildPeople(),
        entry,
        useQueue: false
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.ok) {
        this.reloadTimeEntries();
        this.snackBar.open(entry ? 'Stunden aktualisiert' : 'Stunden erfasst', 'OK', { duration: 3000 });
      }
    });
  }

  get submittedCount(): number {
    return this.timeEntries.filter(t => t.status === 'Submitted').length;
  }

  /** Alle noch nicht freigegebenen Stunden auf einmal freigeben (Bauleiter). */
  approveAll(): void {
    if (!this.canManage || this.submittedCount === 0) return;
    this.stammkundeService.approveAllTimeEntries(this.auftragId).subscribe({
      next: res => {
        this.snackBar.open(`${res.approved} Eintrag/Einträge freigegeben`, 'OK', { duration: 3000 });
        this.loadTimeEntries();
      },
      error: err => this.snackBar.open(
        err?.error?.message || 'Freigabe fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  approveEntry(entry: ZeitEintrag, event: Event): void {
    event.stopPropagation();
    if (!this.canManage) return;
    this.stammkundeService.approveTimeEntry(this.auftragId, entry.id).subscribe({
      next: updated => {
        this.applyEntry(updated);
        this.snackBar.open('Stunden freigegeben', 'OK', { duration: 3000 });
      },
      error: err => this.snackBar.open(
        err?.error?.message || 'Freigabe fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  reopenEntry(entry: ZeitEintrag, event: Event): void {
    event.stopPropagation();
    if (!this.canManage) return;
    this.stammkundeService.reopenTimeEntry(this.auftragId, entry.id).subscribe({
      next: updated => {
        this.applyEntry(updated);
        this.snackBar.open('Freigabe zurückgenommen', 'OK', { duration: 3000 });
      },
      error: err => this.snackBar.open(
        err?.error?.message || 'Aktion fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  deleteEntry(entry: ZeitEintrag, event: Event): void {
    event.stopPropagation();
    if (!this.canManage) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Stunden löschen', message: `Eintrag (${entry.hours} h · ${entry.personName}) wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService.deleteTimeEntry(this.auftragId, entry.id).subscribe({
          next: () => {
            this.timeEntries = this.timeEntries.filter(t => t.id !== entry.id);
            this.rebuildTimeGroups();
            this.snackBar.open('Stunden gelöscht', 'OK', { duration: 3000 });
          },
          error: err => this.snackBar.open(
            err?.error?.message || 'Löschen fehlgeschlagen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  private applyEntry(updated: ZeitEintrag): void {
    this.timeEntries = this.timeEntries.map(t => t.id === updated.id ? updated : t);
    this.rebuildTimeGroups();
  }

  private loadTimeEntries(): void {
    this.stammkundeService.getTimeEntries(this.auftragId).subscribe({
      next: entries => {
        this.timeEntries = entries ?? [];
        this.rebuildTimeGroups();
      },
      error: () => { /* z.B. Tabelle noch nicht migriert – Bereich bleibt leer */ }
    });
  }

  private reloadTimeEntries(): void {
    this.loadTimeEntries();
    // Status kann durch die erste Stundenmeldung auf "In Arbeit" springen.
    this.stammkundeService.getAuftrag(this.auftragId).subscribe({
      next: auftrag => this.auftrag = auftrag,
      error: () => { /* Anzeige bleibt unverändert */ }
    });
  }

  trackByEntry(_: number, t: ZeitEintrag): number {
    return t.id;
  }

  get hasMaterials(): boolean {
    return this.materialTotals.length > 0;
  }

  // ── Material-Freigabe (analog zu den Stunden) ──

  get materialSubmittedCount(): number {
    return this.assignments.reduce(
      (sum, e) => sum + (e.materials ?? []).filter(m => (m.status ?? 'Submitted') === 'Submitted').length, 0);
  }

  materialStatusLabel(status?: string): string {
    return (this.materialStatusLabels as Record<string, string>)[status ?? 'Submitted'] ?? (status ?? '');
  }

  approveMaterialLine(m: EinsatzMaterial, event: Event): void {
    event.stopPropagation();
    if (!this.canManage || m.id == null) return;
    this.stammkundeService.approveMaterial(this.auftragId, m.id).subscribe({
      next: () => { this.reloadAssignments(); this.snackBar.open('Material freigegeben', 'OK', { duration: 2500 }); },
      error: err => this.snackBar.open(err?.error?.message || 'Freigabe fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  reopenMaterialLine(m: EinsatzMaterial, event: Event): void {
    event.stopPropagation();
    if (!this.canManage || m.id == null) return;
    this.stammkundeService.reopenMaterial(this.auftragId, m.id).subscribe({
      next: () => { this.reloadAssignments(); this.snackBar.open('Freigabe zurückgenommen', 'OK', { duration: 2500 }); },
      error: err => this.snackBar.open(err?.error?.message || 'Aktion fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  approveAllMaterials(): void {
    if (!this.canManage || this.materialSubmittedCount === 0) return;
    this.stammkundeService.approveAllMaterials(this.auftragId).subscribe({
      next: res => {
        this.snackBar.open(`${res.approved} Materialposten freigegeben`, 'OK', { duration: 3000 });
        this.reloadAssignments();
      },
      error: err => this.snackBar.open(err?.error?.message || 'Freigabe fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  /** Kurzzusammenfassung des Materials für die Einsatz-Karte (max. 2 Positionen). */
  materialSummary(e: AuftragAssignment): string {
    const list = e.materials ?? [];
    const shown = list.slice(0, 2).map(m => `${m.quantity} ${m.unit} ${m.name}`);
    const more = list.length - shown.length;
    return shown.join(', ') + (more > 0 ? ` +${more}` : '');
  }

  trackByDay(_: number, g: { day: Date }): number {
    return g.day.getTime();
  }

  trackByEinsatz(_: number, e: AuftragAssignment): number {
    return e.id;
  }

  editAuftrag(): void {
    if (!this.auftrag) return;
    this.stammkundeService.getLinkedWarehouses(this.customerId).subscribe({
      next: warehouses => {
        const dialogRef = this.dialog.open(AuftragFormComponent, {
          width: '760px',
          maxWidth: '96vw',
          data: { customerId: this.customerId, auftrag: this.auftrag, linkedWarehouses: warehouses ?? [] }
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.auftrag = result;
            this.snackBar.open('Auftrag aktualisiert', 'OK', { duration: 3000 });
          }
        });
      },
      error: () => this.snackBar.open('Lager konnten nicht geladen werden', 'OK', { duration: 4000 })
    });
  }

  openEinsatzForm(assignment?: AuftragAssignment): void {
    const dialogRef = this.dialog.open(EinsatzFormComponent, {
      width: '640px',
      maxWidth: '96vw',
      data: {
        auftrag: this.auftrag,
        assignment,
        users: this.users,
        workers: this.workers
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.reloadAssignments();
        this.snackBar.open(assignment ? 'Einsatz aktualisiert' : 'Einsatz geplant', 'OK', { duration: 3000 });
      }
    });
  }

  deleteAssignment(assignment: AuftragAssignment, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Einsatz löschen',
        message: assignment.inviteSent
          ? 'Einsatz wirklich löschen? Es wurde bereits eine Kalender-Einladung verschickt – eine Absage wird mitgesendet.'
          : 'Einsatz wirklich löschen?'
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService
          .deleteAssignment(this.auftragId, assignment.id, assignment.inviteSent)
          .subscribe({
            next: () => {
              this.reloadAssignments();
              this.snackBar.open('Einsatz gelöscht', 'OK', { duration: 3000 });
            },
            error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
          });
      }
    });
  }

  private reloadAssignments(): void {
    this.stammkundeService.getAssignments(this.auftragId).subscribe({
      next: assignments => {
        this.assignments = assignments ?? [];
        this.rebuildDayGroups();
      },
      error: () => { /* Liste bleibt unverändert */ }
    });

    // Auftrag neu laden (Status kann durch Planung auf "Geplant" springen).
    this.stammkundeService.getAuftrag(this.auftragId).subscribe({
      next: auftrag => this.auftrag = auftrag,
      error: () => { /* Anzeige bleibt unverändert */ }
    });
  }

  workStatusLabel(status: string): string {
    switch (status) {
      case 'Planned': return 'Geplant';
      case 'Confirmed': return 'Bestätigt';
      case 'EnRoute': return 'Unterwegs';
      case 'Done': return 'Erledigt';
      default: return status;
    }
  }

  openPlanung(): void {
    this.router.navigate(['/stammkunden', this.customerId, 'planung']);
  }

  back(): void {
    this.router.navigate(['/stammkunden', this.customerId]);
  }
}
